import { PAYMENT_METHODS } from '../../config/payment.js';
import { state, patch, subscribe } from '../../state/store.js';
import { escapeHtml } from '../../lib/format.js';
import { PAYMENT_LOGOS } from './logos.js';

const $root = () => document.getElementById('payment-root');

function isDisabledFor(method, deliveryMethodId) {
  return Array.isArray(method.incompatibleDelivery)
      && method.incompatibleDelivery.includes(deliveryMethodId);
}

function noticeFor(method, deliveryMethodId) {
  if (!method.notices || !deliveryMethodId) return null;
  return method.notices[deliveryMethodId] || null;
}

/**
 * If the currently-selected payment is no longer compatible with the
 * delivery method, jump to the first compatible one so we never leave the
 * UI in a "selected-but-disabled" state.
 */
function autoSwitchIfDisabled() {
  const deliveryId = state.delivery?.methodId;
  const current = PAYMENT_METHODS.find((m) => m.id === state.payment.methodId);
  if (!current || !isDisabledFor(current, deliveryId)) return;
  const fallback = PAYMENT_METHODS.find((m) => !isDisabledFor(m, deliveryId));
  if (fallback) patch('payment', { methodId: fallback.id });
}

/**
 * Does the cart contain a line flagged for this method's `cartNotice`?
 * Values come straight from the Shopify product metafield, so anything the
 * config doesn't list (including `yes` and an unset/empty value) is normal
 * and shows nothing.
 */
function cartNoticeApplies(p) {
  const cfg = p.cartNotice;
  if (!cfg?.lineField || !Array.isArray(cfg.flagValues)) return false;
  const flags = cfg.flagValues.map((v) => String(v).toLowerCase());
  return (state.cart?.lines || []).some((l) => {
    const v = String(l?.[cfg.lineField] ?? '').trim().toLowerCase();
    return v !== '' && flags.includes(v);
  });
}

/**
 * Highlighted, informational note inside the method's box. Never disables the
 * method and never blocks checkout — the manager follows up if needed.
 */
function cartNoticeHtml(p) {
  const cfg = p.cartNotice;
  if (!cfg) return '';
  const link = cfg.linkUrl && cfg.linkText
    ? `<a href="${escapeHtml(cfg.linkUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(cfg.linkText)}</a>`
    : escapeHtml(cfg.linkText || '');
  return `
        <div class="pay-cart-notice">
          <span>${escapeHtml(cfg.text || '')}</span>
          <span>${escapeHtml(cfg.linkPrefix || '')}${link}${escapeHtml(cfg.linkSuffix || '')}</span>
        </div>`;
}

/** Row of acceptance chips (Visa, Mastercard, …). Unknown keys are skipped. */
function logosHtml(keys) {
  const chips = (keys || [])
    .map((k) => PAYMENT_LOGOS[k])
    .filter(Boolean)
    .map((l) => `<span class="pay-logo" role="img" aria-label="${escapeHtml(l.label)}">${l.html}</span>`)
    .join('');
  return chips ? `<div class="pay-logos">${chips}</div>` : '';
}

/** "Безпечна оплата через [plata] by mono" — small trust line. */
function footnoteHtml(f) {
  if (!f?.text) return '';
  const mark = f.mark ? ` <span class="pay-mark pay-mark-sm">${escapeHtml(f.mark)}</span>` : '';
  const suffix = f.suffix ? ` <span class="pay-footnote-brand">${escapeHtml(f.suffix)}</span>` : '';
  return `<div class="pay-footnote">${escapeHtml(f.text)}${mark}${suffix}</div>`;
}

/**
 * Body of one payment method. Methods that declare `logos` get the stacked
 * card layout (title / logo chips / footnote); the rest keep the compact
 * one-line layout. Driven entirely by config — see src/config/payment.js.
 */
function bodyHtml(p) {
  if (Array.isArray(p.logos) && p.logos.length) {
    return `
      <div class="pay-body pay-body-rich">
        <span class="pay-label">${escapeHtml(p.title || p.label)}</span>
        ${logosHtml(p.logos)}
        ${footnoteHtml(p.footnote)}
      </div>`;
  }
  const markHtml = p.mark ? `<span class="pay-mark">${escapeHtml(p.mark)}</span>` : '';
  const descHtml = p.desc ? `<span class="pay-desc">— ${escapeHtml(p.desc)}</span>` : '';
  return `
      <div class="pay-body">
        ${markHtml}
        <span class="pay-label">${escapeHtml(p.title || p.label)}</span>
        ${descHtml}
      </div>`;
}

function render() {
  const root = $root();
  if (!root) return;
  const deliveryId = state.delivery?.methodId;

  root.innerHTML = PAYMENT_METHODS.map((p) => {
    const isSelected = state.payment.methodId === p.id;
    const disabled   = isDisabledFor(p, deliveryId);
    const notice     = noticeFor(p, deliveryId);
    // Only while this method is the chosen one — it explains what happens if
    // you pay this way, so it is noise next to an unselected option.
    const cartNote   = (isSelected && !disabled && cartNoticeApplies(p)) ? cartNoticeHtml(p) : '';
    const noticeHtml = notice
      ? `<div class="pay-notice${disabled ? ' pay-notice-warn' : ''}">${escapeHtml(notice)}</div>`
      : '';

    return `
      <div class="pay${isSelected && !disabled ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}"
           data-id="${p.id}"
           data-disabled="${disabled ? '1' : '0'}"
           ${disabled ? 'aria-disabled="true"' : ''}>
        <span class="radio"></span>
        ${bodyHtml(p)}
        ${cartNote}
        ${noticeHtml}
      </div>
    `;
  }).join('');

  root.querySelectorAll('.pay').forEach((el) => {
    el.addEventListener('click', () => {
      if (el.dataset.disabled === '1') return;
      patch('payment', { methodId: el.dataset.id });
    });
  });
}

/**
 * Lazy-load the provider module for the currently-selected payment method.
 * Called by main.js when the user hits "Сплатити".
 */
export async function getActiveProvider() {
  const method = PAYMENT_METHODS.find((m) => m.id === state.payment.methodId);
  if (!method?.provider) return null;
  return await method.provider();
}

export function initPayment() {
  render();
  subscribe(['payment'], render);
  // The prepayment notice depends on what is in the cart, so re-render when
  // lines are added/removed (e.g. an upsell) — not just on method changes.
  subscribe(['cart'], render);

  // Re-render + auto-switch when the user changes the delivery carrier.
  // Gated to method-id change only so we don't rebuild on every keystroke
  // in city/branch inputs.
  let lastDeliveryMethodId = state.delivery?.methodId;
  subscribe(['delivery'], () => {
    const id = state.delivery?.methodId;
    if (id === lastDeliveryMethodId) return;
    lastDeliveryMethodId = id;
    autoSwitchIfDisabled();
    render();
  });
}
